import {
  FcClipped,
  FcColor,
  FcColorBlack,
  FcColorType,
  FcElement,
  FcGradientType,
  FcGrid,
  FcGridImageData,
  FcGridProps,
  FcModule,
  FcModuleImageInfo,
  FcPipeType,
  FcPoint,
  FcPositionProperty,
  FcRandomShapeColor,
  FcRandomShapeColorType,
  FcRandomShapes,
  FcReservedArea,
  FcRgb,
  FcShape,
  FcSvg,
  RsGroup,
  fcBuildSvgColor,
  fcBuildSvgColorForImageFill,
  fcColorDifference,
  fcGetColorLabel,
  fcSvgColorToNumber
} from '@dtx-company/flowcode-generator-types/src'
import { fcBuildFlowcode, fcChangeSvgColor, fcImageAddSize, fcImageToTag } from './'
import { fcGenerateGradient } from './fc_gradient'
import { fcGetDomId } from './fc_dom'
import { roundDecimal } from './fc_utils'

interface FcLoadedImages {
  imageData: string[]
}

interface FcBorderPosition {
  module: FcModule
  position: FcPositionProperty
}

interface FcPointPosition {
  point: FcPoint
  position: FcPositionProperty
}

interface FcClusterInfo {
  data: boolean
  count: number
  erased: boolean
}

interface FcModuleAndGroup {
  module: FcModule
  rsGroup: RsGroup
}

class FcGridRendererProps {
  props: FcGridProps
  imagesCreated: Map<string, boolean> = new Map<string, boolean>()
  moduleImages: FcLoadedImages = { imageData: [] }
  moduleImageJointData = new Map<FcPipeType, FcLoadedImages>()
  constructor(props: FcGridProps) {
    this.props = props
  }
}

export class FcGridRenderer {
  grid: FcGrid
  idBase: string
  diamondOffset = 0
  shaggyOffset = 0
  circXOffset = 0
  circYOffset = 0
  diamondWidth = 0
  shaggyWidth = 0
  circRadius = 0
  xsize = 0
  ysize = 0
  w = 0
  h = 0
  cx = 0
  cy = 0
  mx = 0
  my = 0
  mradius = 0
  dataProps: FcGridRendererProps
  outerProps: FcGridRendererProps
  shapesCreated: Map<string, boolean> = new Map<string, boolean>()
  colorInds: Map<string, number> = new Map<string, number>()
  colorIndMax = 0
  sizePadding = 0
  allGridImageData?: FcGridImageData[]
  hasOverlays = false
  clusterInfo: Map<number, FcClusterInfo>
  gridRandomShapeColorIds: string[] = []
  outerRandomShapeColorIds: string[] = []
  randomShapeDefs: Map<string, string>
  hasTextBlend = false
  logoInCode = false
  logoCodeBitmap?: number[][]

  constructor(
    grid: FcGrid,
    sizePadding: number,
    gridLogoData?: FcGridImageData,
    overlayData?: FcGridImageData[],
    textBlend?: FcGridImageData,
    logoInCode?: boolean,
    logoCodeBitmap?: number[][]
  ) {
    this.grid = grid
    this.idBase = fcGetDomId()
    this.dataProps = new FcGridRendererProps(grid.dataProps)
    this.outerProps = new FcGridRendererProps(grid.outerProps)
    this.sizePadding = sizePadding
    this.clusterInfo = new Map<number, FcClusterInfo>()
    this.randomShapeDefs = new Map<string, string>()
    this.logoInCode = logoInCode || false
    this.logoCodeBitmap = logoCodeBitmap
    if (gridLogoData || overlayData || textBlend) {
      this.allGridImageData = []
      if (gridLogoData) {
        this.allGridImageData.push(gridLogoData)
      }
      if (overlayData) {
        this.hasOverlays = overlayData.length > 0
        this.allGridImageData.push(...overlayData)
      }
      if (textBlend) {
        this.hasTextBlend = true
        this.allGridImageData.push(textBlend)
      }
    }
  }

  getProps(y: number, x: number): FcGridRendererProps {
    if (this.grid.inDataGrid(y, x)) {
      return this.dataProps
    }
    return this.outerProps
  }

  colorIsSimilar(color: FcColor | undefined, pixel: FcRgb, tolerance: number): boolean {
    return color !== undefined && fcColorDifference(fcSvgColorToNumber(color), pixel) <= tolerance
  }

  getPixel(module: FcModule, px: number, py: number): FcRgb | undefined {
    if (module.inBlendPixelSet === undefined) {
      if (module.blendData) {
        module.inBlendPixel = module.blendData?.rawImage.getPixel(
          px,
          py,
          px + this.xsize,
          py + this.ysize,
          module.blendData?.blendModulePixelsToCheck
        )
      } else if (this.allGridImageData) {
        const len = this.allGridImageData?.length
        for (let i = 0; i < len; i++) {
          const imageData = this.allGridImageData[i]
          module.inBlendPixel = imageData.rawImage.getPixel(
            px,
            py,
            px + this.xsize,
            py + this.ysize,
            imageData.blendModulePixelsToCheck
          )
          if (module.inBlendPixel !== undefined) {
            module.blendData = imageData
            break
          }
        }
      }
      module.inBlendPixelSet = true
    }
    return module.inBlendPixel
  }

  getColorInd(color?: FcColor): number {
    if (!color) {
      return 0
    }
    const val = this.colorInds.get(color)
    if (val) {
      return val
    }
    const valAssign = ++this.colorIndMax
    this.colorInds.set(color, valAssign)
    return valAssign
  }

  getShape(
    useCmyk: boolean,
    x: number,
    y: number,
    px: number,
    py: number,
    gradient: number,
    isSet: boolean
  ): string {
    const module = this.grid.modules[y][x]
    let modrect = ''
    let xgradientPad = 0
    let ygradientPad = 0
    let skipDraw = false
    let blendDraw = false

    if (module.forceErased) {
      return modrect
    }
    const props = this.getProps(y, x)
    if (this.moduleInBlend(module, px, py)) {
      const pixel = this.getPixel(module, px, py)
      if (pixel !== undefined) {
        blendDraw = true
        if (module.set) {
          if (
            this.colorIsSimilar(module.color, pixel, module.blendData!.blendModuleTolerationFg!)
          ) {
            skipDraw = true
          }
        } else {
          if (
            this.colorIsSimilar(
              this.grid.dataProps.backgroundColor,
              pixel,
              module.blendData!.blendModuleTolerationBg!
            )
          ) {
            skipDraw = true
          }
        }
      } else {
        blendDraw = true
      }
    } else if (module.blendData?.blend && !module.set) {
      return modrect
    }

    if (skipDraw) {
      return modrect
    }

    if (blendDraw) {
      module.shape = module.blendData!.blendModuleShape!
      gradient = module.blendData!.blendModuleSize!
      if (!module.set) {
        module.color = module.blendData!.blendModuleColorBg!
      } else {
        module.color = module.blendData!.blendModuleColorFg!
      }
    }

    if (gradient != 1) {
      xgradientPad = ((1 - gradient) * this.xsize) / 2
      ygradientPad = ((1 - gradient) * this.ysize) / 2
    }

    const hasModuleImage = blendDraw
      ? module.blendData?.hasBlendModuleImage
      : props.moduleImages.imageData.length > 0 ||
        (props.moduleImageJointData.size &&
          module.pipeType &&
          props.moduleImageJointData.get(module.pipeType.type))

    const shape = hasModuleImage ? FcShape.CUSTOM : module.shape
    const modId = `s${shape}-${gradient}-${this.getColorInd(module.color)}-${this.idBase}`.replace(
      /\./g,
      ''
    )
    switch (shape) {
      case FcShape.CIRCLE:
        if (!this.shapesCreated.get(modId)) {
          this.shapesCreated.set(modId, true)
          const data = `<circle id="${modId}" cx="${roundDecimal(
            this.circXOffset
          )}" cy="${roundDecimal(this.circYOffset)}" r="${roundDecimal(
            this.circRadius * gradient
          )}" ${fcBuildSvgColor(module.color, FcColorType.FILL, useCmyk)} />`
          modrect = `<defs>${data}</defs>`
        }
        modrect += `<use x="${roundDecimal(px)}" y="${roundDecimal(py)}" xlink:href="#${modId}"/>`
        break
      case FcShape.DIAMOND:
        const w = this.diamondWidth * gradient
        if (!this.shapesCreated.get(modId)) {
          this.shapesCreated.set(modId, true)
          const data = `<rect id="${modId}" x="${roundDecimal(
            this.diamondOffset + xgradientPad
          )}" y="${roundDecimal(this.diamondOffset + ygradientPad)}" width="${roundDecimal(
            w
          )}" height="${roundDecimal(w)}" ${fcBuildSvgColor(
            module.color,
            FcColorType.FILL,
            useCmyk
          )} transform="rotate(45 ${roundDecimal(
            this.diamondOffset + xgradientPad + w / 2
          )} ${roundDecimal(this.diamondOffset + ygradientPad + w / 2)})"/>`
          modrect = `<defs>${data}</defs>`
        }
        modrect += `<use x="${roundDecimal(px)}" y="${roundDecimal(py)}" xlink:href="#${modId}"/>`
        break
      case FcShape.SHAGGY:
        let y = py + ygradientPad
        let height = this.ysize * gradient
        if (module.extendDown) {
          height += this.grid.emptySpaceSize
        } else if (module.extendUp) {
          y -= this.grid.emptySpaceSize
          height += this.grid.emptySpaceSize
        }
        if (!this.shapesCreated.get(modId)) {
          this.shapesCreated.set(modId, true)
          const data = `<rect id="${modId}" width="${roundDecimal(
            this.shaggyWidth * gradient
          )}" height="${roundDecimal(height)}" ${fcBuildSvgColor(
            module.color,
            FcColorType.FILL,
            useCmyk
          )} shape-rendering="crispEdges"/>`
          modrect = `<defs>${data}</defs>`
        }
        modrect += `<use x="${roundDecimal(
          px + this.shaggyOffset + xgradientPad
        )}" y="${roundDecimal(y)}" xlink:href="#${modId}"/>`
        break
      case FcShape.CUSTOM:
        let col = ''
        if (blendDraw && module.blendData!.blendModuleColorFillSvg && module.color) {
          col = fcGetColorLabel(module.color!)
        } else if (props.props.fillSvg && module.color) {
          col = fcGetColorLabel(module.color!)
        }
        const imgType = props.props.outer ? 1 : 0
        let ind = 100
        let rotate = 0
        let moduleImages = props.moduleImages
        let pipeKey = ''
        if (!blendDraw) {
          const pipeType = module.pipeType
          if (pipeType) {
            const pipeData = props.moduleImageJointData.get(pipeType.type)
            if (pipeData && pipeData.imageData.length) {
              pipeKey = `-${pipeType.type}-${pipeType.rotate}`
              rotate = pipeType.rotate
              moduleImages = pipeData
            }
          }
          ind =
            moduleImages.imageData.length === 0
              ? 0
              : Math.floor(Math.random() * moduleImages.imageData.length)
        }
        const imgId = `img-${gradient}-${ind}-${imgType}-${col}${pipeKey}-${this.idBase}`.replace(
          /\./g,
          ''
        )
        if (!props.imagesCreated.get(imgId)) {
          props.imagesCreated.set(imgId, true)
          let data = blendDraw
            ? module.blendData?.blendModuleImageData
            : moduleImages.imageData[ind]
          // TODO: Remove `data || ''` to make calls more typesafe
          if (col) {
            data = fcChangeSvgColor(data || '', fcBuildSvgColorForImageFill(module.color!, useCmyk))
          }
          const sizedData = fcImageAddSize(
            data || '',
            0,
            0,
            this.xsize * gradient,
            this.ysize * gradient,
            imgId,
            undefined,
            rotate
          )
          modrect = `<defs>${sizedData}</defs>`
        }
        modrect += `<use x="${roundDecimal(px + xgradientPad)}" y="${roundDecimal(
          py + ygradientPad
        )}" xlink:href="#${imgId}"/>`
        break
      case FcShape.SQUARE:
      default:
        if (!this.shapesCreated.get(modId)) {
          this.shapesCreated.set(modId, true)
          const data = `<rect id="${modId}" width="${roundDecimal(
            this.xsize * gradient
          )}" height="${roundDecimal(this.ysize * gradient)}" ${fcBuildSvgColor(
            module.color,
            FcColorType.FILL,
            useCmyk
          )} shape-rendering="crispEdges"/>`
          modrect = `<defs>${data}</defs>`
        }
        modrect += `<use x="${roundDecimal(px + xgradientPad)}" y="${roundDecimal(
          py + ygradientPad
        )}" xlink:href="#${modId}"/>`
        break
    }
    return modrect
  }

  adjustIncr(value: number, increment: number): number {
    value -= increment
    if (value < 0) {
      value = 0
    } else if (value > 1) {
      value = 1
    }
    return value
  }

  shouldUseGradient(y: number, x: number, container: boolean, shared: boolean): boolean {
    if (!this.grid.modules[y][x].set) {
      return false
    }
    if (shared) {
      return true
    }
    return this.grid.inDataGrid(y, x) === !container
  }

  getPx(x: number): number {
    return x * this.xsize + this.grid.getEmptyOffset(x) - this.sizePadding
  }

  getPy(y: number): number {
    return y * this.ysize + this.grid.getEmptyOffset(y) - this.sizePadding
  }

  getDistance(y: number, x: number): number {
    return Math.sqrt(Math.pow(x - this.mx, 2) + Math.pow(y - this.my, 2))
  }

  applyGradient(container: boolean): void {
    const props = container ? this.outerProps : this.dataProps
    const gradient = props.props.gradient
    let length = this.grid.totalModuleCount
    let startInd = 0
    let endInd = length

    if (!gradient) {
      if (!container) {
        endInd = this.grid.dataModuleOffset + this.grid.dataModuleCount
        startInd = this.grid.dataModuleOffset
      }
      if (props.props.numRandomSizes) {
        const num = props.props.numRandomSizes
        for (let y = startInd; y < endInd; y++) {
          for (let x = startInd; x < endInd; x++) {
            if (this.shouldUseGradient(y, x, container, false)) {
              const ind = Math.floor(Math.random() * num)
              this.grid.modules[y][x].gradient = props.props.randomSizes[ind]
            }
          }
        }
      }
      return
    }
    if (container && gradient.type === FcGradientType.SHARED_WITH_DATA) {
      return
    }

    const shared =
      (!container &&
        this.outerProps.props.gradient &&
        this.outerProps.props.gradient.type === FcGradientType.SHARED_WITH_DATA) ||
      false
    if (!container && !shared) {
      endInd = this.grid.dataModuleOffset + this.grid.dataModuleCount
      startInd = this.grid.dataModuleOffset
      length = this.grid.dataModuleCount
    }
    const start = gradient.startPercent / 100
    const end = gradient.endPercent / 100
    const freq = Math.floor(length / gradient.numberSizes)
    const increment = (((start - end) / (gradient.numberSizes - 1)) * 10) / 10
    let value = start
    let n = 0

    switch (gradient.type) {
      case FcGradientType.RADIAL:
        for (let y = startInd; y < endInd; y++) {
          for (let x = startInd; x < endInd; x++) {
            if (this.shouldUseGradient(y, x, container, shared)) {
              const distance = this.getDistance(y, x)
              let perc: number
              if (increment > 0) {
                perc = distance / this.mradius
              } else {
                perc = 1 - distance / this.mradius
              }
              const sz = perc * (gradient.numberSizes - 1)
              if (perc >= 0) {
                if (increment > 0) {
                  this.grid.modules[y][x].gradient = start - sz * increment
                } else {
                  this.grid.modules[y][x].gradient = end + sz * increment
                }
              }
            }
          }
        }
        break
      case FcGradientType.LINEAR_BOTTOM:
        for (let y = endInd - 1; y >= startInd; y--) {
          for (let x = startInd; x < endInd; x++) {
            if (this.shouldUseGradient(y, x, container, shared)) {
              this.grid.modules[y][x].gradient = value
            }
          }
          if (++n === freq) {
            value = this.adjustIncr(value, increment)
            n = 0
          }
        }
        break
      case FcGradientType.LINEAR_LEFT:
        for (let x = startInd; x < endInd; x++) {
          for (let y = startInd; y < endInd; y++) {
            if (this.shouldUseGradient(y, x, container, shared)) {
              this.grid.modules[y][x].gradient = value
            }
          }
          if (++n === freq) {
            value = this.adjustIncr(value, increment)
            n = 0
          }
        }
        break
      case FcGradientType.LINEAR_TOP:
        for (let y = startInd; y < endInd; y++) {
          for (let x = startInd; x < endInd; x++) {
            if (this.shouldUseGradient(y, x, container, shared)) {
              this.grid.modules[y][x].gradient = value
            }
          }
          if (++n === freq) {
            value = this.adjustIncr(value, increment)
            n = 0
          }
        }
        break
      case FcGradientType.LINEAR_RIGHT:
        for (let x = endInd - 1; x >= startInd; x--) {
          for (let y = startInd; y < endInd; y++) {
            if (this.shouldUseGradient(y, x, container, shared)) {
              this.grid.modules[y][x].gradient = value
            }
          }
          if (++n === freq) {
            value = this.adjustIncr(value, increment)
            n = 0
          }
        }
        break
    }
  }

  calculateOffsets(svg: FcSvg): void {
    this.w = svg.width + this.sizePadding * 2
    this.h = svg.height + this.sizePadding * 2
    this.cx = this.w / 2 - this.sizePadding
    this.cy = this.h / 2 - this.sizePadding
    const length = this.grid.totalModuleCount
    this.mx = length / 2 - this.sizePadding
    this.my = length / 2 - this.sizePadding
    this.mradius = length / 2

    const xsize = this.grid.getSize(this.w)
    const ysize = this.grid.getSize(this.h)

    this.xsize = xsize
    this.ysize = ysize
    this.grid.xsize = xsize
    this.grid.ysize = ysize
  }

  clipModules(
    radius: number,
    joinBorder: boolean,
    allModules: boolean,
    reserved?: FcReservedArea
  ): void {
    const length = this.grid.totalModuleCount
    for (let y = 0; y < length; y++) {
      for (let x = 0; x < length; x++) {
        const module = this.grid.modules[y][x]
        if (allModules || module.type === FcElement.CONTAINER) {
          const px = this.getPx(x)
          const py = this.getPy(y)
          if (reserved && reserved.inBox(px, py, this.xsize, this.ysize)) {
            continue
          }
          this.setClip(px, py, radius, module)
        }
      }
    }
    if (!joinBorder) {
      return
    }
    for (let y = 0; y < length; y++) {
      for (let x = 0; x < length; x++) {
        const module = this.grid.modules[y][x]
        const props = this.getProps(y, x)
        if (
          module.set &&
          props.moduleImages.imageData.length === 0 &&
          module.shape === FcShape.SHAGGY &&
          (allModules || module.type === FcElement.CONTAINER) &&
          module.clipped === FcClipped.PARTIAL
        ) {
          this.checkJoin(x, y)
        }
      }
    }
  }

  checkJoin(x: number, y: number): void {
    const length = this.grid.totalModuleCount
    if (y === 0 || y === length - 1) {
      return
    }
    if (y < length / 2) {
      this.grid.modules[y][x].extendUp =
        y >= this.grid.dataModuleOffset && y - 1 < this.grid.dataModuleOffset
      y = y - 1
      while (
        y >= 0 &&
        !this.grid.modules[y][x].set &&
        this.grid.modules[y][x].clipped === FcClipped.PARTIAL
      ) {
        this.grid.modules[y][x].set = true
        y = y - 1
      }
    } else {
      this.grid.modules[y][x].extendDown =
        y < this.grid.dataModuleBoundary && y + 1 >= this.grid.dataModuleBoundary
      y = y + 1
      while (
        y < length &&
        !this.grid.modules[y][x].set &&
        this.grid.modules[y][x].clipped === FcClipped.PARTIAL
      ) {
        this.grid.modules[y][x].set = true
        y = y + 1
      }
    }
  }

  setClip(x: number, y: number, radius: number, module: FcModule): void {
    let w = this.xsize
    let h = this.ysize
    let num = 0

    if (module.shape === FcShape.SHAGGY) {
      const xgradientPad = 0
      const ygradientPad = 0
      x = x + this.shaggyOffset + xgradientPad
      y = y + ygradientPad
      w = this.shaggyWidth
      h = this.ysize
    }

    let dist = Math.sqrt((this.cx - x) ** 2 + (this.cy - y) ** 2)
    if (dist > radius) {
      num++
    }
    dist = Math.sqrt((this.cx - (x + w)) ** 2 + (this.cy - y) ** 2)
    if (dist > radius) {
      num++
    }
    dist = Math.sqrt((this.cx - x) ** 2 + (this.cy - (y + h)) ** 2)
    if (dist > radius) {
      num++
    }
    dist = Math.sqrt((this.cx - (x + w)) ** 2 + (this.cy - (y + h)) ** 2)
    if (dist > radius) {
      num++
    }
    if (num === 4) {
      module.clipped = FcClipped.FULL
    } else if (num) {
      module.clipped = FcClipped.PARTIAL
    }
  }

  async getBlendModuleImages(useCmyk: boolean): Promise<void> {
    if (this.allGridImageData) {
      const len = this.allGridImageData.length
      for (let i = 0; i < len; i++) {
        const overlay = this.allGridImageData[i]
        if (overlay.blendModuleImageUrl) {
          overlay.blendModuleImageData = await fcImageToTag(useCmyk, overlay.blendModuleImageUrl)
          overlay.hasBlendModuleImage = true
        }
      }
    }
  }

  async loadImages(useCmyk: boolean, moduleInfo?: FcModuleImageInfo): Promise<FcLoadedImages> {
    const loadedImages: FcLoadedImages = { imageData: [] }
    if (!moduleInfo || moduleInfo.urls.length === 0) {
      return loadedImages
    }
    const num = moduleInfo.urls.length
    for (let i = 0; i < num; i++) {
      loadedImages.imageData.push(await fcImageToTag(useCmyk, moduleInfo.urls[i]))
    }
    return loadedImages
  }

  async loadShapeColors(useCmyk: boolean, props: FcGridProps, ids: string[]): Promise<string> {
    if (!props.randomShapes?.enable) {
      return ''
    }
    let defs = ''
    const num = props.randomShapes.colors?.length
    for (let i = 0; i < num; i++) {
      const color = props.randomShapes.colors[i]
      const id = `${this.idBase}-rs-${i}`
      ids[i] = FcColorBlack
      switch (color.clusterColorType) {
        case FcRandomShapeColorType.GRADIENT:
          if (color.gradient) {
            const key = JSON.stringify(color.gradient)
            const defId = this.randomShapeDefs.get(key)
            if (defId) {
              ids[i] = defId
            } else {
              const gradientDef = fcGenerateGradient(useCmyk, color.gradient, id, false)
              defs += gradientDef
              ids[i] = `url(#${id})`
              this.randomShapeDefs.set(key, ids[i])
            }
          }
          break
        case FcRandomShapeColorType.SOLID:
          if (color.color) {
            ids[i] = color.color
          }
          break
        case FcRandomShapeColorType.TEXTURE:
          if (color.textureImageUrl) {
            const key = JSON.stringify(color.gradient)
            const defId = this.randomShapeDefs.get(key)
            if (defId) {
              ids[i] = defId
            } else {
              const img = await fcImageToTag(useCmyk, color.textureImageUrl)
              const size = color.textureImageSize || 40
              const sizedImg = fcImageAddSize(img, 0, 0, size, size, fcGetDomId())
              defs += `<pattern id="${id}" width="${size}" height="${size}" patternUnits="userSpaceOnUse">${sizedImg}</pattern>`
              ids[i] = `url(#${id})`
              this.randomShapeDefs.set(key, ids[i])
            }
          }
          break
      }
    }
    return defs
  }

  async getModuleImages(props: FcGridRendererProps, useCmyk: boolean): Promise<void> {
    props.moduleImages = await this.loadImages(useCmyk, props.props.moduleImageInfo)
    if (props.props.pipeImageUrls) {
      const pipeTypes = [
        FcPipeType.ELBOW,
        FcPipeType.COUPLE,
        FcPipeType.TEE,
        FcPipeType.CROSS,
        FcPipeType.SINGLE,
        FcPipeType.CAP
      ]
      for (const pipeType of pipeTypes) {
        const imageInfo = props.props.pipeImageUrls.get(pipeType)
        if (imageInfo && imageInfo.urls.length > 0) {
          props.moduleImageJointData.set(pipeType, await this.loadImages(useCmyk, imageInfo))
        }
      }
    }
  }

  findErased(reserved?: FcReservedArea): void {
    const exact = this.grid.erasedExact || false
    const length = this.grid.totalModuleCount
    let hasErasures = false

    for (let y = 0; y < length; y++) {
      for (let x = 0; x < length; x++) {
        const module = this.grid.modules[y][x]
        const px = this.getPx(x)
        const py = this.getPy(y)
        if (
          module.clipped === FcClipped.FULL ||
          (reserved &&
            reserved.inBox(px, py, this.xsize, this.ysize) &&
            !this.moduleInBlend(module, px, py))
        ) {
          module.pipeErased = true
        }
        if (module.type === FcElement.POSITION || module.type == FcElement.CONTAINER) {
          continue
        }
        if (module.clipped === FcClipped.FULL) {
          if (module.type === FcElement.DATA) {
            module.erased = true
          }
          continue
        }
        if (
          reserved &&
          reserved.inBox(px, py, this.xsize, this.ysize) &&
          !this.moduleInBlend(module, px, py)
        ) {
          if (module.type === FcElement.DATA) {
            let pixel: FcRgb | undefined
            if (exact && (pixel = this.getPixel(module, px, py))) {
              const tolerance = this.grid.erasedExactTolerance || 0
              if (module.set) {
                if (!this.colorIsSimilar(module.color, pixel, tolerance)) {
                  module.erased = true
                }
              } else {
                if (!this.colorIsSimilar(this.grid.dataProps.backgroundColor, pixel, tolerance)) {
                  module.erased = true
                }
              }
            } else {
              module.erased = true
            }
          }
          continue
        }
        if (module.clipped === FcClipped.PARTIAL && module.type === FcElement.DATA) {
          module.erased = true
          continue
        }
        if (
          module.type === FcElement.DATA &&
          this.moduleInBlend(module, px, py) &&
          module.blendData?.blendErase
        ) {
          const pixel = this.getPixel(module, px, py)
          if (pixel) {
            if (module.set) {
              if (
                !this.colorIsSimilar(
                  module.color,
                  pixel,
                  module.blendData!.blendModuleTolerationFg!
                )
              ) {
                module.inBlendCanBeErased = true
                hasErasures = true
              }
            } else {
              if (
                !this.colorIsSimilar(
                  this.grid.dataProps.backgroundColor,
                  pixel,
                  module.blendData!.blendModuleTolerationBg!
                )
              ) {
                module.inBlendCanBeErased = true
                hasErasures = true
                if (this.hasTextBlend && (pixel.r !== 255 || pixel.g !== 255 || pixel.b !== 255)) {
                  module.overText = true
                }
              }
            }
          } else if (this.hasTextBlend) {
            module.inBlendCanBeErased = true
            hasErasures = true
          }
        }
      }
    }

    const targets: FcModuleAndGroup[] = []
    if (hasErasures || this.grid.dataProps.randomShapes?.enable) {
      const rsGroups = this.grid.getErasedRsGroups()
      for (let y = 0; y < length; y++) {
        for (let x = 0; x < length; x++) {
          const module = this.grid.modules[y][x]
          if (
            module.type !== FcElement.DATA ||
            (!module.inBlendCanBeErased && !module.inClusterCanBeErased)
          ) {
            continue
          }
          const rs = module.rsGroup === undefined ? -1 : module.rsGroup
          if (rs === -1 || module.byteIndex == undefined) {
            continue
          }
          const rsGroup = rsGroups.get(rs)
          if (!rsGroup) {
            continue
          }
          const byteIndex = module.byteIndex
          if (byteIndex === undefined) {
            continue
          }

          targets.push({ module, rsGroup })
        }
      }

      let currentIndex = targets.length,
        randomIndex
      while (currentIndex != 0) {
        randomIndex = Math.floor(Math.random() * currentIndex)
        currentIndex--
        ;[targets[currentIndex], targets[randomIndex]] = [
          targets[randomIndex],
          targets[currentIndex]
        ]
      }
      if (this.hasTextBlend) {
        targets.sort((a, b) => {
          return (b.module.overText ? 1 : 0) - (a.module.overText ? 1 : 0)
        })
      }
      targets.forEach(target => {
        const rsGroup = target.rsGroup
        const module = target.module
        const byteIndex = module.byteIndex!
        if (!rsGroup.badBytes.has(byteIndex)) {
          if (rsGroup.numBadBytes >= rsGroup.allowedErrors - (this.grid.calcBlendErasurePad || 0)) {
            return
          }
          rsGroup.numBadBytes++
          rsGroup.badBytes.add(byteIndex)
        }
        if (module.inClusterCanBeErased) {
          const cluster = this.clusterInfo.get(module.clusterNumber!)
          if (cluster) {
            cluster.erased = true
          }
        }
        module.erased = true
        module.forceErased = true
      })
      // go back through again an mark any erased in bytes erased in loop above
      targets.forEach(target => {
        const rsGroup = target.rsGroup
        const module = target.module
        const byteIndex = module.byteIndex!
        if (rsGroup.badBytes.has(byteIndex)) {
          if (module.inClusterCanBeErased) {
            const cluster = this.clusterInfo.get(module.clusterNumber!)
            if (cluster) {
              cluster.erased = true
            }
          }
          module.erased = true
          module.forceErased = true
        }
      })
    }
  }

  async renderModules(
    svg: FcSvg,
    reserved?: FcReservedArea,
    radius?: number,
    inset?: boolean,
    dataCheckOnly?: boolean
  ): Promise<void> {
    let clipAllModules = inset || false
    this.calculateOffsets(svg)
    await this.getModuleImages(this.dataProps, svg.getUseCmyk())
    await this.getModuleImages(this.outerProps, svg.getUseCmyk())
    await this.getBlendModuleImages(svg.getUseCmyk())

    if (this.logoInCode) {
      const length = this.grid.totalModuleCount
      let logoCodeBitmapX = 0
      let logoCodeBitmapY = 0
      const midLength = Math.ceil(length / 2)
      if (this.logoCodeBitmap) {
        if (this.grid.rotate) {
          const rotBitmap: number[][] = []
          const rows = this.logoCodeBitmap[0].length
          const cols = this.logoCodeBitmap.length
          for (let i = 0; i < rows; i++) {
            const newRow: number[] = []
            for (let j = cols - 1; j >= 0; j--) {
              newRow.push(this.logoCodeBitmap[j][i])
            }
            rotBitmap.push(newRow)
          }
          this.logoCodeBitmap = rotBitmap
        }
        logoCodeBitmapY = midLength - Math.ceil(this.logoCodeBitmap.length / 2)
        logoCodeBitmapX = midLength - Math.ceil(this.logoCodeBitmap[0].length / 2)
      }
      for (let y = 0; y < length; y++) {
        for (let x = 0; x < length; x++) {
          const module = this.grid.modules[y][x]
          if (module.isPad) {
            if (this.logoCodeBitmap) {
              const bitX = x - logoCodeBitmapX
              const bitY = y - logoCodeBitmapY
              if (
                bitY >= 0 &&
                bitY < this.logoCodeBitmap.length &&
                bitX >= 0 &&
                bitX < this.logoCodeBitmap[bitY].length
              ) {
                module.set = this.logoCodeBitmap[bitY][bitX] === 1
              } else {
                module.set = false
              }
            } else {
              const px = this.getPx(x)
              const py = this.getPy(y)
              const pixel = this.getPixel(module, px, py)
              if (
                pixel !== undefined &&
                (pixel?.r !== 255 || pixel?.g !== 255 || pixel?.b !== 255)
              ) {
                module.set = true
              } else {
                module.set = false
              }
            }
          }
        }
      }
      fcBuildFlowcode(this.grid.data, this.grid, true, true)
    }

    if (radius && this.grid.radiusMask) {
      let radiusMask = this.grid.radiusMask
      if (this.hasOverlays) {
        radiusMask -= this.xsize
      }
      const newRadius = (svg.width * radiusMask) / 200
      if (newRadius < radius) {
        clipAllModules = true
        radius = newRadius
      }
    }

    this.diamondWidth = this.xsize / Math.sqrt(2)
    this.shaggyWidth = (this.xsize * 2) / 3
    this.circRadius = this.xsize / 2

    this.calculateOffset(this.xsize, this.ysize)
    if (radius) {
      const joinBorder =
        this.outerProps.props.baseModuleShape === FcShape.SHAGGY && !this.grid.radiusMask
      this.clipModules(radius, joinBorder, clipAllModules, reserved)
    }
    if (dataCheckOnly) {
      this.findErased(reserved)
      return
    }
    this.applyGradient(true)
    this.applyGradient(false)
    const length = this.grid.totalModuleCount

    const gridRandomShapes = this.grid.dataProps.randomShapes?.enable || false
    const outerRandomShapes = this.grid.outerProps.randomShapes?.enable || false
    if (gridRandomShapes || outerRandomShapes) {
      let str = await this.loadShapeColors(
        svg.useCmyk,
        this.grid.dataProps,
        this.gridRandomShapeColorIds
      )
      str += await this.loadShapeColors(
        svg.useCmyk,
        this.grid.outerProps,
        this.outerRandomShapeColorIds
      )
      if (str !== '') {
        svg.addToDom(`<defs>${str}</defs>`)
      }
      str = this.generateShapes(svg.useCmyk, gridRandomShapes, outerRandomShapes, reserved)
      svg.addToDom(str)
      if (gridRandomShapes && outerRandomShapes) {
        return
      }
    }

    if (!gridRandomShapes) {
      this.findErased(reserved)
    }
    this.grid.applyPipes()

    let modrect = ''
    for (let y = 0; y < length; y++) {
      for (let x = 0; x < length; x++) {
        const props = this.getProps(y, x)

        const hasGradient = props.props.gradient || props.props.numRandomSizes || false
        const module = this.grid.modules[y][x]
        if (module.type === FcElement.POSITION) {
          if (this.hasTextBlend) {
            const px = this.getPx(x)
            const py = this.getPy(y)
            if (this.moduleInBlend(module, px, py)) {
              svg.centerTextClipped = true
            }
          }
          continue
        }
        if (module.type === FcElement.CONTAINER) {
          if (outerRandomShapes) {
            continue
          }
        } else if (gridRandomShapes) {
          continue
        }
        if (module.clipped === FcClipped.FULL) {
          continue
        }

        const px = this.getPx(x)
        const py = this.getPy(y)
        if (
          reserved &&
          reserved.inBox(px, py, this.xsize, this.ysize) &&
          (module.type !== FcElement.DATA || !this.moduleInBlend(module, px, py))
        ) {
          continue
        }

        if (!module.set && !module.blendData?.blend) {
          continue
        }
        modrect += this.getShape(
          svg.getUseCmyk(),
          x,
          y,
          px,
          py,
          hasGradient ? module.gradient! : 1,
          module.set || false
        )
      }
    }

    svg.addToDom(modrect)
  }

  moduleInBlend(module: FcModule, px: number, py: number): boolean | undefined {
    if (module.inBlend !== undefined) {
      return module.inBlend
    }
    if (this.allGridImageData) {
      const len = this.allGridImageData.length
      for (let i = 0; i < len; i++) {
        const imageData = this.allGridImageData[i]
        if (imageData.blend && imageData.blendMask?.inBox(px, py, this.xsize, this.ysize)) {
          module.inBlend = true
          module.blendData = imageData
          break
        }
      }
    } else {
      module.inBlend = false
    }
    return module.inBlend
  }

  calculateOffset(xsize: number, ysize: number): void {
    this.circXOffset = xsize / 2
    this.circYOffset = ysize / 2
    this.diamondOffset = (xsize - xsize / Math.sqrt(2)) / 2
    this.shaggyOffset = xsize / 6
  }

  isSameElementType(type1: FcElement, type2: FcElement): boolean {
    if (type1 === FcElement.CONTAINER || type2 === FcElement.CONTAINER) {
      return type1 === type2
    }
    if (type1 === FcElement.POSITION || type2 === FcElement.POSITION) {
      return false
    }
    return true
  }

  checkNeighbor(
    clusterNumber: number,
    x: number,
    y: number,
    elementType: FcElement,
    reserved?: FcReservedArea
  ): void {
    if (x < 0 || x >= this.grid.totalModuleCount) {
      return
    }
    if (y < 0 || y >= this.grid.totalModuleCount) {
      return
    }
    const px = this.getPx(x)
    const py = this.getPy(y)
    if (reserved && reserved.inBox(px, py, this.xsize, this.ysize)) {
      return
    }
    const module = this.grid.modules[y][x]
    if (
      !module.set ||
      !this.isSameElementType(module.type!, elementType) ||
      module.clusterNumber !== undefined
    ) {
      return
    }
    const info = this.clusterInfo.get(clusterNumber)
    if (info) {
      info.count++
    } else {
      this.clusterInfo.set(clusterNumber, {
        data: module.type !== FcElement.CONTAINER,
        count: 1,
        erased: false
      })
    }

    module.clusterNumber = clusterNumber
    this.checkNeighbor(clusterNumber, x + 1, y, elementType, reserved)
    this.checkNeighbor(clusterNumber, x - 1, y, elementType, reserved)
    this.checkNeighbor(clusterNumber, x, y + 1, elementType, reserved)
    this.checkNeighbor(clusterNumber, x, y - 1, elementType, reserved)
  }

  assignClusters(doGrid: boolean, doOuter: boolean, reserved?: FcReservedArea): void {
    const length = this.grid.totalModuleCount
    for (let y = 0; y < length; y++) {
      for (let x = 0; x < length; x++) {
        const module = this.grid.modules[y][x]
        if (module.type === FcElement.CONTAINER) {
          if (!doOuter) {
            continue
          }
        } else if (!doGrid) {
          continue
        }
        if (module.set && module.clusterNumber === undefined) {
          const num = this.clusterInfo.size
          this.checkNeighbor(num, x, y, module.type!, reserved)
        }
      }
    }
  }

  isPointInCluster(x: number, y: number, clusterNumber: number): boolean {
    if (x < 0 || x >= this.grid.totalModuleCount) {
      return false
    }
    if (y < 0 || y >= this.grid.totalModuleCount) {
      return false
    }
    const module = this.grid.modules[y][x]
    return module.clusterNumber == clusterNumber
  }

  getClusterBorderPoints(module: FcModule, clusterNumber: number): FcPositionProperty[] {
    const positions: FcPositionProperty[] = []
    const x = module.col
    const y = module.row
    if (!this.isPointInCluster(x, y, clusterNumber)) {
      return positions
    }
    if (!this.isPointInCluster(x + 1, y, clusterNumber)) {
      positions.push(FcPositionProperty.RIGHT)
    }
    if (!this.isPointInCluster(x - 1, y, clusterNumber)) {
      positions.push(FcPositionProperty.LEFT)
    }
    if (!this.isPointInCluster(x, y - 1, clusterNumber)) {
      positions.push(FcPositionProperty.TOP)
    }
    if (!this.isPointInCluster(x, y + 1, clusterNumber)) {
      positions.push(FcPositionProperty.BOTTOM)
    }
    return positions
  }

  getBorderPoint(
    adjust: number,
    randomShapes: FcRandomShapes,
    entry: FcBorderPosition
  ): FcPoint | undefined {
    const x = entry.module.col
    const y = entry.module.row
    const px = this.getPx(x)
    const py = this.getPy(y)
    if (adjust > 1) {
      adjust = 1
    }
    const high = adjust
    const low = 1 - adjust
    switch (entry.position) {
      case FcPositionProperty.RIGHT:
        return new FcPoint(px + this.xsize * high, py + this.ysize / 2)
      case FcPositionProperty.LEFT:
        return new FcPoint(px + this.xsize * low, py + this.ysize / 2)
      case FcPositionProperty.TOP:
        return new FcPoint(px + this.xsize / 2, py + this.xsize * low)
      case FcPositionProperty.BOTTOM:
        return new FcPoint(px + this.xsize / 2, py + this.ysize * high)
      default:
        throw new Error(`invalid position: ${entry.position}`)
    }
  }

  getNextBorder(
    clusterNumber: number,
    entry: FcBorderPosition,
    modules: Map<number, FcBorderPosition>
  ): FcBorderPosition | undefined {
    const x = entry.module.col
    const y = entry.module.row
    const pos = entry.position
    switch (pos) {
      case FcPositionProperty.TOP:
        return (
          this.checkBorderModule(clusterNumber, x + 1, y, FcPositionProperty.TOP, modules) ||
          this.checkBorderModule(clusterNumber, x - 1, y, FcPositionProperty.TOP, modules) ||
          this.checkBorderModule(clusterNumber, x, y, FcPositionProperty.RIGHT, modules) ||
          this.checkBorderModule(clusterNumber, x, y, FcPositionProperty.LEFT, modules) ||
          this.checkBorderModule(
            clusterNumber,
            x + 1,
            y - 1,
            FcPositionProperty.LEFT,
            modules,
            new FcPoint(x + 1, y)
          ) ||
          this.checkBorderModule(
            clusterNumber,
            x - 1,
            y - 1,
            FcPositionProperty.RIGHT,
            modules,
            new FcPoint(x - 1, y)
          )
        )
      case FcPositionProperty.BOTTOM:
        return (
          this.checkBorderModule(clusterNumber, x + 1, y, FcPositionProperty.BOTTOM, modules) ||
          this.checkBorderModule(clusterNumber, x - 1, y, FcPositionProperty.BOTTOM, modules) ||
          this.checkBorderModule(clusterNumber, x, y, FcPositionProperty.RIGHT, modules) ||
          this.checkBorderModule(clusterNumber, x, y, FcPositionProperty.LEFT, modules) ||
          this.checkBorderModule(
            clusterNumber,
            x + 1,
            y + 1,
            FcPositionProperty.LEFT,
            modules,
            new FcPoint(x + 1, y)
          ) ||
          this.checkBorderModule(
            clusterNumber,
            x - 1,
            y + 1,
            FcPositionProperty.RIGHT,
            modules,
            new FcPoint(x - 1, y)
          )
        )
      case FcPositionProperty.LEFT:
        return (
          this.checkBorderModule(clusterNumber, x, y + 1, FcPositionProperty.LEFT, modules) ||
          this.checkBorderModule(clusterNumber, x, y - 1, FcPositionProperty.LEFT, modules) ||
          this.checkBorderModule(clusterNumber, x, y, FcPositionProperty.TOP, modules) ||
          this.checkBorderModule(clusterNumber, x, y, FcPositionProperty.BOTTOM, modules) ||
          this.checkBorderModule(
            clusterNumber,
            x - 1,
            y + 1,
            FcPositionProperty.TOP,
            modules,
            new FcPoint(x, y + 1)
          ) ||
          this.checkBorderModule(
            clusterNumber,
            x - 1,
            y - 1,
            FcPositionProperty.BOTTOM,
            modules,
            new FcPoint(x, y - 1)
          )
        )
      case FcPositionProperty.RIGHT:
        return (
          this.checkBorderModule(clusterNumber, x, y + 1, FcPositionProperty.RIGHT, modules) ||
          this.checkBorderModule(clusterNumber, x, y - 1, FcPositionProperty.RIGHT, modules) ||
          this.checkBorderModule(clusterNumber, x, y, FcPositionProperty.TOP, modules) ||
          this.checkBorderModule(clusterNumber, x, y, FcPositionProperty.BOTTOM, modules) ||
          this.checkBorderModule(
            clusterNumber,
            x + 1,
            y + 1,
            FcPositionProperty.TOP,
            modules,
            new FcPoint(x, y + 1)
          ) ||
          this.checkBorderModule(
            clusterNumber,
            x + 1,
            y - 1,
            FcPositionProperty.BOTTOM,
            modules,
            new FcPoint(x, y - 1)
          )
        )
      default:
        throw new Error(`invalid position: ${entry.position}`)
    }
  }

  getModuleMapKey(x: number, y: number, pos: FcPositionProperty): number {
    return ((y * this.grid.totalModuleCount + x) << 16) + pos
  }

  checkBorderModule(
    clusterNumber: number,
    x: number,
    y: number,
    position: FcPositionProperty,
    modules: Map<number, FcBorderPosition>,
    mustExist?: FcPoint
  ): FcBorderPosition | undefined {
    const key = this.getModuleMapKey(x, y, position)
    const mod = modules.get(key)
    if (!mod) {
      return undefined
    }

    if (
      mustExist &&
      mustExist.x >= 0 &&
      mustExist.x < this.grid.totalModuleCount &&
      mustExist.y >= 0 &&
      mustExist.y < this.grid.totalModuleCount
    ) {
      const module = this.grid.modules[mustExist.y][mustExist.x]
      if (
        (!module.set || module.clusterNumber !== clusterNumber) &&
        this.isSameElementType(module.type!, mod.module.type!)
      ) {
        return undefined
      }
    }
    return mod
  }

  getClusterModules(clusterNumber: number): FcPoint[] {
    const length = this.grid.totalModuleCount
    const points: FcPoint[] = []
    for (let y = 0; y < length; y++) {
      for (let x = 0; x < length; x++) {
        const module = this.grid.modules[y][x]
        if (module.clusterNumber == clusterNumber) {
          points.push(new FcPoint(module.col, module.row))
        }
      }
    }
    return points
  }

  getBoundary(randomShapes: FcRandomShapes, clusterNumber: number): FcPointPosition[][] {
    const pointSets: FcPointPosition[][] = []
    const length = this.grid.totalModuleCount
    const modules = new Map<number, FcBorderPosition>()
    let entry: FcBorderPosition | undefined
    for (let y = 0; y < length; y++) {
      for (let x = 0; x < length; x++) {
        const module = this.grid.modules[y][x]
        const positions = this.getClusterBorderPoints(module, clusterNumber)
        positions.forEach(position => {
          if (!entry) {
            entry = { module, position }
          }
          modules.set(this.getModuleMapKey(x, y, position), { module, position })
        })
      }
    }
    const total = modules.size
    const iter = modules.values()
    while (entry) {
      const points: FcPointPosition[] = []
      let adjust = 1
      if (total > 4 && randomShapes.pointFactor !== undefined && randomShapes.pointFactor <= 100) {
        adjust = 0.7 + (0.3 * Math.random() * randomShapes.pointFactor) / 100
      }

      while (entry) {
        modules.delete(this.getModuleMapKey(entry.module.col, entry.module.row, entry.position))
        const point = this.getBorderPoint(adjust, randomShapes, entry)
        if (point) {
          points.push({ point, position: entry.position })
        }
        entry = this.getNextBorder(clusterNumber, entry, modules)
      }
      pointSets.push(points)
      if (modules.size) {
        const nxt = iter.next()
        if (!nxt.done) {
          entry = nxt.value as FcBorderPosition
        }
      }
    }
    return pointSets
  }

  eraseClusters(): void {
    for (let x = this.grid.dataModuleOffset; x < this.grid.dataModuleBoundary; x++) {
      for (let y = this.grid.dataModuleOffset; y < this.grid.dataModuleBoundary; y++) {
        const module = this.grid.modules[y][x]
        if (module.set && module.type === FcElement.DATA && module.clusterNumber !== undefined) {
          const cluster = this.clusterInfo.get(module.clusterNumber)
          if (cluster && cluster.count === 1) {
            module.inClusterCanBeErased = true
          }
        }
      }
    }
  }

  generateShapes(
    useCmyk: boolean,
    doGrid: boolean,
    doOuter: boolean,
    reserved?: FcReservedArea
  ): string {
    let paths = ''
    let appendPath = ''
    this.assignClusters(doGrid, doOuter, reserved)
    if (this.grid.dataProps.randomShapes?.eraseSmallClusters) {
      this.eraseClusters()
      this.findErased(reserved)
    }
    const numClusters = this.clusterInfo.size
    for (let i = 0; i < numClusters; i++) {
      const cluster = this.clusterInfo.get(i)
      if (!cluster) {
        continue
      }
      const randomShapes = cluster.data
        ? this.grid.dataProps.randomShapes
        : this.grid.outerProps.randomShapes
      if (!randomShapes) {
        continue
      }
      if (cluster.erased) {
        continue
      }
      if (randomShapes.eraseSmallClusters && cluster.count === 1 && !cluster.data) {
        continue
      }
      const small = cluster.count <= 4
      const pointSets = this.getBoundary(randomShapes, i)
      const pathstr = this.pointsToPath(i, randomShapes, useCmyk, pointSets)
      if (small) {
        appendPath += pathstr
      } else {
        paths += pathstr
      }
    }
    paths += appendPath
    return paths
  }

  getControlPoint(
    randomShapes: FcRandomShapes,
    point1: FcPointPosition,
    point2: FcPointPosition
  ): FcPoint {
    const p1 = point1.point
    const p2 = point2.point
    const x = p1.x + (p2.x - p1.x) / 2
    const y = p1.y + (p2.y - p1.y) / 2
    let curveFactor = 0.25
    if (randomShapes.curveFactor !== undefined) {
      curveFactor = Math.min(randomShapes.curveFactor / 100, 1)
    }
    const fact = Math.random() * this.xsize * curveFactor
    if (p1.x == p2.x) {
      return new FcPoint(x + (point2.position === FcPositionProperty.LEFT ? -fact : fact), y)
    } else if (p1.y === p2.y) {
      return new FcPoint(x, y + (point2.position === FcPositionProperty.TOP ? -fact : fact))
    } else if (point1.position !== point2.position) {
      const xfact =
        point1.position === FcPositionProperty.RIGHT || point2.position === FcPositionProperty.RIGHT
          ? fact
          : -fact
      const yfact =
        point1.position === FcPositionProperty.BOTTOM ||
        point2.position === FcPositionProperty.BOTTOM
          ? fact
          : -fact
      return new FcPoint(x + xfact, y + yfact)
    } else {
      const isBefore = (p1.x < p2.x && p1.y > p2.y) || (p1.x > p2.x && p1.y < p2.y)
      switch (point1.position) {
        case FcPositionProperty.TOP:
          return new FcPoint(x + (isBefore ? -fact : fact), y - fact)
        case FcPositionProperty.BOTTOM:
          return new FcPoint(x + (isBefore ? fact : -fact), y + fact)
        case FcPositionProperty.RIGHT:
          return new FcPoint(x + fact, y + (isBefore ? fact : -fact))
        case FcPositionProperty.LEFT:
          return new FcPoint(x - fact, y + (isBefore ? -fact : fact))
        default:
          return new FcPoint(x + fact, y + fact)
      }
    }
  }

  isSameSign(x1: number, x2: number): boolean {
    if (x1 === 0) {
      return x2 === 0
    }
    return x1 > 0 ? x2 > 0 : x2 < 0
  }

  pointsToPath(
    clusterNumber: number,
    randomShapes: FcRandomShapes,
    useCmyk: boolean,
    pointSets: FcPointPosition[][]
  ): string {
    const pointSetsLen = pointSets.length
    if (pointSetsLen === 0) {
      return ''
    }
    const cluster = this.clusterInfo.get(clusterNumber)
    if (!cluster) {
      return ''
    }

    const randomShapeColorIds = cluster.data
      ? this.gridRandomShapeColorIds
      : this.outerRandomShapeColorIds
    let col: FcRandomShapeColor | undefined
    let colNumber = 0
    if (randomShapes.colors) {
      colNumber = clusterNumber % randomShapes.colors?.length
      col = randomShapes.colors[colNumber]
    }
    if (!col) {
      return ''
    }
    const colorStr = randomShapeColorIds[colNumber]
    let path = `<path d="`

    let lineFactor = 0
    if (randomShapes.lineFactor !== undefined) {
      lineFactor = randomShapes.lineFactor / 100
    }

    for (let p = 0; p < pointSetsLen; p++) {
      const points = pointSets[p]
      const len = points.length
      const firstPoint = points[0]
      let start = firstPoint
      let prev = firstPoint
      if (p) {
        path += ' '
      }
      path += `M ${roundDecimal(firstPoint.point.x)} ${roundDecimal(firstPoint.point.y)}`
      let prevDir: FcPoint | undefined = undefined
      for (let i = 1; i < len; i++) {
        const point = points[i].point
        const dir = new FcPoint(
          +(point.x - prev.point.x).toFixed(4),
          +(point.y - prev.point.y).toFixed(4)
        )
        const keep = Math.random() >= lineFactor
        if (
          keep &&
          (!prevDir || (this.isSameSign(dir.x, prevDir.x) && this.isSameSign(dir.y, prevDir.y)))
        ) {
          prev = points[i]
          prevDir = dir
        } else {
          const cpoint = this.getControlPoint(randomShapes, start, prev)
          path += ` Q ${roundDecimal(cpoint.x)} ${roundDecimal(cpoint.y)},${roundDecimal(
            prev.point.x
          )} ${roundDecimal(prev.point.y)}`
          start = prev
          prev = points[i]
          prevDir = dir
        }
      }
      let cpoint = this.getControlPoint(randomShapes, start, prev)
      path += ` Q ${roundDecimal(cpoint.x)} ${roundDecimal(cpoint.y)},${roundDecimal(
        prev.point.x
      )} ${roundDecimal(prev.point.y)}`
      start = prev
      cpoint = this.getControlPoint(randomShapes, start, firstPoint)
      path += ` Q ${roundDecimal(cpoint.x)} ${roundDecimal(cpoint.y)},${roundDecimal(
        firstPoint.point.x
      )} ${roundDecimal(firstPoint.point.y)}`
    }
    path += `" fill-rule="evenodd" stroke-width="${roundDecimal(
      col.outlineSize || 0
    )}" ${fcBuildSvgColor(
      col.outlineColor || '#000000',
      FcColorType.STROKE,
      useCmyk
    )} ${fcBuildSvgColor(`${colorStr}`, FcColorType.FILL, useCmyk)}/>`
    return path
  }
}
